/* eslint-disable no-nested-ternary */
import React, { useState, useEffect } from 'react'
import { useForm, Controller, ErrorMessage } from 'react-hook-form'
import { FaRegFileAlt, FaRegImage } from 'react-icons/fa'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import TextField from '@material-ui/core/TextField'
import ProductsModal from '~/components/Sponsor/Modals/ProductsModal'
import Border from '~/components/Shared/Border'
import useSponsorFunctions from '~/helper/sponsorFunctions'
import { UploadS3 } from '~/helper/formFunctions'
import CreateFormatMobileVisualization from '~/components/CreateFormatMobileVisualization'
import ContentVisualization from '~/components/CreateFormatMobileVisualization/Content'
import { StartDateSection, EndDateSection } from '~/components/Shared/CreateFormatDateSection'
import { InputTitle, FormatUploadButton } from '~/components/Shared/StyledComponents'
import { transformDateToUtcFormat } from '~/helper/getFormattedDates'

import * as S from './styled'

export default function ModalEditDocument({
  handleCloseModal,
  showModal,
  documentToUpdate,
  review,
  createMessage,
  refuseCampaignProduct,
  approveCampaignProduct,
  sponsor,
  formatId,
}) {
  const { t } = useTranslation()
  const currentCampaignForReviewPage = useSelector(state => state.createCampaign.currentCampaignForReviewPage)
  const { updateCampaignFormatAction } = useSponsorFunctions()
  const { image, title, about } = documentToUpdate
  const [updatingImage, setUpdatingImage] = useState(false)
  const [updatingFile, setUpdatingFile] = useState(false)
  const [loadingUpload, setLoadingUpload] = useState(false)
  const [file, setFile] = useState(documentToUpdate.file)
  const [fileUrl, setFileUrl] = useState(documentToUpdate.fileUrl)
  const [imageId, setImageId] = useState(image)
  const [imageUrl, setImageUrl] = useState(documentToUpdate.imageUrl)
  const [date, setDate] = useState(transformDateToUtcFormat(documentToUpdate.date))
  const [endDate, setEndDate] = useState(transformDateToUtcFormat(documentToUpdate.endDate))
  const { handleSubmit, setValue, register, control, errors, watch } = useForm()
  const titleWatch = watch('title', documentToUpdate.title)
  const aboutWatch = watch('about', documentToUpdate.about)

  function onSubmit(product) {
    const copyObject = JSON.stringify(documentToUpdate)
    const productCopy = JSON.stringify(product)
    if (copyObject === productCopy) {
      return
    }
    updateCampaignFormatAction(product, formatId, handleCloseModal)
  }

  function modalReviewCreateMessageAndCloseModal() {
    createMessage()
    refuseCampaignProduct()
    handleCloseModal()
  }

  function handleUpdatingFile() {
    setUpdatingFile(!updatingFile)
  }

  function handleUpdatingImage() {
    setUpdatingImage(!updatingImage)
  }

  useEffect(() => {
    register({ name: 'id' })
    register({ name: 'file' })
    register({ name: 'fileUrl' })
    register({ name: 'image' })
    register({ name: 'imageUrl' })
    setValue('image', imageId)
    setValue('imageUrl', imageUrl)
    setValue('file', file)
    setValue('fileUrl', fileUrl)
    setValue('id', documentToUpdate.id)
  }, [register, documentToUpdate.id, setValue, file, fileUrl, imageId, imageUrl])

  return (
    <ProductsModal
      createMessage={modalReviewCreateMessageAndCloseModal}
      approveCampaignProduct={approveCampaignProduct}
      action={onSubmit}
      buttonText={review ? t('done') : t('update')}
      modalTitle={review ? t('reviseFormat', { format: t('document') }) : t('editFormat', { format: t('document') })}
      handleCloseModal={handleCloseModal}
      showModal={showModal}
      loadingUpload={loadingUpload}
      review={review}
      sponsor={sponsor}
      leftColumn={
        <form id="my-form" onSubmit={handleSubmit(onSubmit)}>
          <S.FormContainer>
            <InputTitle>{t('title')}</InputTitle>
            <Controller
              as={<TextField fullWidth variant="outlined" placeholder={t('typeTitle')} disabled={review || false} />}
              rules={{ required: t('defaultValidation', { inputName: t('title') }) }}
              name="title"
              control={control}
              defaultValue={title === null ? '' : title}
            />
            <div className="error-message">
              <ErrorMessage errors={errors} name="title" as="p" />
            </div>

            <StartDateSection
              product={t('document')}
              selectedCampaignStartDate={date}
              setCampaignStartDate={setDate}
              campaignStartDate={currentCampaignForReviewPage.dtStart}
              campaignEndDate={currentCampaignForReviewPage.dtEnd}
              disabled={review}
              control={control}
              errors={errors}
            />
            <EndDateSection
              product={t('document')}
              selectedCampaignStartDate={date}
              selectedCampaignEndDate={endDate}
              campaignEndDate={currentCampaignForReviewPage.dtEnd}
              setCampaignEndDate={setEndDate}
              disabled={review}
              control={control}
              errors={errors}
            />

            <div className="mt-3">
              <InputTitle>{t('about')}</InputTitle>
              <Controller
                as={
                  <TextField
                    fullWidth
                    multiline
                    rows="4"
                    variant="outlined"
                    placeholder={t('moreAboutContent', { format: t('post').toLocaleLowerCase() })}
                    disabled={review || false}
                  />
                }
                defaultValue={about === null ? '' : about}
                name="about"
                control={control}
              />
            </div>

            <div className="mt-4 mb-2">
              <Border />
            </div>

            {review === false ? (
              updatingFile ? (
                <div className="mt-2">
                  <div className="d-flex justify-content-center align-items-center">
                    <FaRegFileAlt className="mr-2" size={14} />
                    <InputTitle>{t('files')}</InputTitle>
                  </div>
                  <UploadS3
                    setUploadId={setFile}
                    setUploadUrl={setFileUrl}
                    setLoadingUpload={setLoadingUpload}
                    fileType="file"
                  />
                </div>
              ) : (
                <div className="d-flex justify-content-center">
                  <FormatUploadButton text={t('addFile')} action={handleUpdatingFile} />
                </div>
              )
            ) : null}

            {review === false ? (
              updatingImage ? (
                <div className="mt-3">
                  <div className="d-flex justify-content-center align-items-center">
                    <FaRegImage className="mr-2" size={14} />
                    <InputTitle>{t('image')}</InputTitle>
                  </div>
                  <UploadS3
                    setUploadId={setImageId}
                    setUploadUrl={setImageUrl}
                    setLoadingUpload={setLoadingUpload}
                    fileType="image"
                  />
                </div>
              ) : (
                <div className="d-flex justify-content-center">
                  <FormatUploadButton text={t('addImage')} action={handleUpdatingImage} />
                </div>
              )
            ) : null}
          </S.FormContainer>
        </form>
      }
      rightColumn={
        <CreateFormatMobileVisualization
          FormatVisualizationComponent={
            <ContentVisualization image={imageUrl} title={titleWatch} about={aboutWatch} contentType={t('document')} />
          }
        />
      }
    />
  )
}
